import React, { useMemo } from 'react';
import { CommandDefinition, bindCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { CommandRegistry } from '../../../concepts/commands/CommandRegistry';
import { Icon, NoIcon } from '../../../concepts/resources/Icon';
import { Menu } from '../../../ui';
import { Resolver, useResolve } from '../../Container';
import { flexLayoutConfigure } from '../../packages/autoLayout/AutoLayoutCommands';
import { hostRevealSelected } from '../../packages/conceal/ConcealCommands';
import { Concealable } from '../../packages/conceal/Concealable';
import { ConcealmentInfo } from '../../packages/conceal/ConcealmentInfo';
import { exportData } from '../../packages/dataExport/ExportCommands';
import { useEditorSessionObserver } from '../EditorSession';
import { Selection } from '../Selection';
import { copy, copyJson, copyStyle } from '../commands/ClipboardCommands';
import { lockSet } from '../commands/ComponentCommands';
import { bringForward, bringToFront, sendBackward, sendToBack } from '../commands/OrderCommands';
import { deleteSelection, selectOthers } from '../commands/SelectionCommands';
import { IsElevated } from '../workflow/IsElevated';
import { ContextMenuProps } from './ContextMenuProps';

const lockSelection = bindCommand(lockSet, [true]);
const unlockSelection = bindCommand(lockSet, [false]);

const useOnCommand = (onClose: () => void) => {
    const dispatch = useResolve(CommandDispatcher);
    return (command: CommandDefinition<any>) => {
        return () => {
            dispatch(command);
            onClose();
        };
    };
};

const CopySubMenu = (onClose: () => void) =>
    () => {
        const onCommand = useOnCommand(onClose);
        return <Menu>
            <Menu.Item onClick={onCommand(copy)}>
                <Icon size="1.2" src="deqo://icons/hero/clipboard-copy" />
                <Menu.ItemText>Copy</Menu.ItemText>
                <Menu.Shortcut keys="CTRL+C"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(copyJson)}>
                <NoIcon size="1.2" />
                Copy Json
                <Menu.Shortcut keys="CTRL+J"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(copyStyle)}>
                <NoIcon size="1.2" />
                Copy Style
                <Menu.Shortcut keys="SHIFT+C"></Menu.Shortcut>
            </Menu.Item>
        </Menu>;
    };

export const ActiveSelectionContextMenu = React.memo(({ onClose }: ContextMenuProps) => {

    const resolver = useResolve(Resolver);
    const registry = useResolve(CommandRegistry);
    const conceal = useEditorSessionObserver(ConcealmentInfo);
    const elevated = useEditorSessionObserver(IsElevated);
    const selection = useEditorSessionObserver(Selection);
    const onCommand = useOnCommand(onClose);

    const commands = useMemo(() => {
        return registry.filter(x => x.ui?.context?.when(resolver));
    }, [conceal, elevated, selection]);

    // TODO: This should be refactored to use commandDef.ui.context metadata to determine
    // what context menu actions are available, rather than hard coding them here
    const canLock = elevated;
    const shouldLock = elevated && !selection.locked();
    const canForceReveal = elevated && conceal.enabled && selection.some(x => x.is(Concealable));


    return <Menu>
        <Menu.Section first>
            {shouldLock &&
                <Menu.Item disabled={!canLock} onClick={onCommand(lockSelection)}>
                    <Icon size="1.2" src="deqo://icons/d2/locked" />
                    <Menu.ItemText>Lock</Menu.ItemText>
                </Menu.Item>
            }
            {!shouldLock &&
                <Menu.Item disabled={!canLock} onClick={onCommand(unlockSelection)}>
                    <Icon size="1.2" src="deqo://icons/d2/unlocked" />
                    <Menu.ItemText>Unlock</Menu.ItemText>
                </Menu.Item>
            }
            {commands?.map(x =>
                <Menu.Item key={x.name} onClick={onCommand(x)}>
                    <NoIcon size="1.2" />
                    <Menu.ItemText>{x.ui?.context?.label ?? x.label}</Menu.ItemText>
                </Menu.Item>)
            }
            <Menu.Item onClick={onCommand(selectOthers)}>
                <NoIcon size="1.2" />
                Select Others
                <Menu.Shortcut keys="CTRL+SHIFT+A"></Menu.Shortcut>
            </Menu.Item>
        </Menu.Section>
        <Menu.Section>
            {canForceReveal && (
                <Menu.Item onClick={onCommand(hostRevealSelected)}>
                    <NoIcon size="1.2" />
                    <Menu.ItemText>
                        Force Reveal
                    </Menu.ItemText>
                </Menu.Item>
            )}
            <Menu.SubMenuItem menu={CopySubMenu(onClose)}>
                <NoIcon size="1.2" />
                <Menu.ItemText>
                    Copy
                </Menu.ItemText>
                <Menu.SubMenuIndicator />
            </Menu.SubMenuItem>
            <Menu.Item onClick={onCommand(bindCommand(deleteSelection, [true]))}>
                <Icon size="1.2" src="deqo://icons/hero/trash" />
                <Menu.ItemText>Delete</Menu.ItemText>
                <Menu.Shortcut keys="Delete"></Menu.Shortcut>
            </Menu.Item>
        </Menu.Section>
        <Menu.Section>
            <Menu.Item onClick={onCommand(flexLayoutConfigure)}>
                <Icon size="1.2" src="deqo://icons/d2/flex" />
                <Menu.ItemText>Arrange & Sort</Menu.ItemText>
                <Menu.Shortcut keys="U"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(bringForward)}>
                <Icon size="1.2" src="deqo://icons/d2/bring-forward" />
                <Menu.ItemText>Bring Forward</Menu.ItemText>
                <Menu.Shortcut keys="]"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(bringToFront)}>
                <Icon size="1.2" src="deqo://icons/d2/bring-to-front" />
                <Menu.ItemText>Bring to Front</Menu.ItemText>
                <Menu.Shortcut keys="Shift+]"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(sendBackward)}>
                <Icon size="1.2" src="deqo://icons/d2/send-backward" />
                <Menu.ItemText>Send Backward</Menu.ItemText>
                <Menu.Shortcut keys="["></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(sendToBack)}>
                <Icon size="1.2" src="deqo://icons/d2/send-to-back" />
                <Menu.ItemText>Send to Back</Menu.ItemText>
                <Menu.Shortcut keys="Shift+["></Menu.Shortcut>
            </Menu.Item>
        </Menu.Section>
        <Menu.Section>
            <Menu.Item onClick={onCommand(exportData)}>
                <Icon size="1.2" src="deqo://icons/hero/upload" />
                <Menu.ItemText>Export...</Menu.ItemText>
                <Menu.Shortcut keys="CTRL+S"></Menu.Shortcut>
            </Menu.Item>
        </Menu.Section>
    </Menu>;
});
